import { supabase } from '../lib/supabase';

export interface NotificationActionResult {
  success: boolean;
  message: string;
  error?: string;
}

export class NotificationActions {
  /**
   * Mark a booking as completed and set the car status to available
   * Used for "Mark as Returned" action on return reminder notifications
   */
  static async markBookingAsReturned(bookingId: string): Promise<NotificationActionResult> {
    try {
      // First, get the booking details to find the car
      const { data: booking, error: bookingFetchError } = await supabase
        .from('bookings')
        .select('id, car_id, status')
        .eq('id', bookingId)
        .single();

      if (bookingFetchError || !booking) {
        return {
          success: false,
          message: 'Booking not found',
          error: bookingFetchError?.message || 'Booking not found'
        };
      }

      // Update booking status to completed
      const { error: bookingUpdateError } = await supabase
        .from('bookings')
        .update({ 
          status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', bookingId);

      if (bookingUpdateError) {
        return {
          success: false,
          message: 'Failed to update booking status',
          error: bookingUpdateError.message
        };
      }

      // Update car status to available
      const { error: carUpdateError } = await supabase
        .from('cars')
        .update({ 
          status: 'available',
          updated_at: new Date().toISOString()
        })
        .eq('id', booking.car_id);

      if (carUpdateError) {
        // Try to rollback the booking status update
        await supabase
          .from('bookings')
          .update({ status: booking.status })
          .eq('id', bookingId);

        return {
          success: false,
          message: 'Failed to update car status',
          error: carUpdateError.message
        };
      }

      // Mark related return notifications as action taken
      // First, find the relevant notifications
      const { data: notifications, error: notificationFetchError } = await supabase
        .from('notifications')
        .select('id')
        .eq('booking_id', bookingId)
        .in('type', ['booking'])
        .ilike('title', '%Return%');

      if (notificationFetchError) {
        console.warn('Failed to fetch return notifications:', notificationFetchError);
      } else if (notifications && notifications.length > 0) {
        // Update each notification by ID
        const notificationIds = notifications.map(n => n.id);
        const { error: notificationUpdateError } = await supabase
          .from('notifications')
          .update({ 
            action_taken: true,
            updated_at: new Date().toISOString()
          })
          .in('id', notificationIds);

        if (notificationUpdateError) {
          console.warn('Failed to update notification status:', notificationUpdateError);
          // Don't fail the entire operation for this
        }
      }

      return {
        success: true,
        message: 'Vehicle marked as returned successfully'
      };
    } catch (error) {
      console.error('Error marking booking as returned:', error);
      return {
        success: false,
        message: 'An unexpected error occurred',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Update car maintenance information
   * Used for handling maintenance-related notifications
   */
  static async updateCarMaintenance(carId: string, maintenanceData: {
    lastOilChangeDate?: string;
    lastOilChangeMileage?: number;
    currentMileage?: number;
    insuranceExpiryDate?: string;
    technicalControlExpiryDate?: string;
    maintenanceNotes?: string;
  }): Promise<NotificationActionResult> {
    try {
      const updateData: any = {
        updated_at: new Date().toISOString()
      };

      if (maintenanceData.lastOilChangeDate) {
        updateData.last_oil_change_date = maintenanceData.lastOilChangeDate;
      }
      if (maintenanceData.lastOilChangeMileage) {
        updateData.last_oil_change_mileage = maintenanceData.lastOilChangeMileage;
      }
      if (maintenanceData.currentMileage) {
        updateData.current_mileage = maintenanceData.currentMileage;
      }
      if (maintenanceData.insuranceExpiryDate) {
        updateData.insurance_expiry_date = maintenanceData.insuranceExpiryDate;
      }
      if (maintenanceData.technicalControlExpiryDate) {
        updateData.technical_control_expiry_date = maintenanceData.technicalControlExpiryDate;
      }
      if (maintenanceData.maintenanceNotes) {
        updateData.maintenance_notes = maintenanceData.maintenanceNotes;
      }

      const { error } = await supabase
        .from('cars')
        .update(updateData)
        .eq('id', carId);

      if (error) {
        return {
          success: false,
          message: 'Failed to update vehicle maintenance information',
          error: error.message
        };
      }

      return {
        success: true,
        message: 'Vehicle maintenance information updated successfully'
      };
    } catch (error) {
      console.error('Error updating car maintenance:', error);
      return {
        success: false,
        message: 'An unexpected error occurred',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Dismiss a notification and mark it as action taken
   */
  static async dismissNotification(notificationId: string): Promise<NotificationActionResult> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ 
          is_dismissed: true,
          action_taken: true,
          dismissed_at: new Date().toISOString()
        })
        .eq('id', notificationId);

      if (error) {
        return {
          success: false,
          message: 'Failed to dismiss notification',
          error: error.message
        };
      }

      return {
        success: true,
        message: 'Notification dismissed successfully'
      };
    } catch (error) {
      console.error('Error dismissing notification:', error);
      return {
        success: false,
        message: 'An unexpected error occurred',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Check if a booking exists and get its details
   */
  static async getBookingDetails(bookingId: string) {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          car:cars(*),
          agency_client:agency_clients!agency_client_id(*)
        `)
        .eq('id', bookingId)
        .single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Check if a car exists and get its details
   */
  static async getCarDetails(carId: string) {
    try {
      const { data, error } = await supabase
        .from('cars')
        .select('*')
        .eq('id', carId)
        .single();

      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }
} 